import {autorun, IReactionDisposer, observable, toJS} from 'mobx';
import {STATIONS} from './train-constants';
import {TrainAPI} from '@/data/train-api';

export const enum RideTags {
  REARGUARD = 'REARGUARD'
}

const noop = () => void 0;

export const enum Dialog {
  NONE = 'NONE',
  TRAIN_RENAME = 'TRAIN_RENAME'
}

const initialState: State = {
  myTrains: [],
  search: {
    searching: false,
    error: '',
    results: null,
    current: {
      fromId: '2800',
      toId: '3700',
      hour: '8:34',
      date: 'today'
    },
    history: {
      fromIds: [],
      toIds: [],
      hour: '',
      date: '',
      favoriteFromStations: {},
      favoriteToStations: {}
    },
    config: {
      hourPresets: [
        {
          label: 'עכשיו',
          value: 'now'
        },
        {
          label: 'חציש',
          value: 'now+30'
        },
        {
          label: 'שעה',
          value: 'now+60'
        },
        {
          label: 'שעתיים',
          value: 'now+120'
        },
        {
          label: '+3',
          value: 'now+180'
        },
        {
          label: '+4',
          value: 'now+240'
        }
      ],
      datePresets: [
        {
          label: 'היום',
          value: 'today'
        },
        {
          label: 'מחר',
          value: 'today+24'
        },
        {
          label: 'מחרתיים',
          value: 'today+48'
        },
        {
          label: '{DAY}',
          value: 'today+72'
        },
        {
          label: '{DAY}',
          value: 'today+96'
        }
      ]
    }
  },
  dialog: Dialog.NONE,
  dialogCb: noop
};
let storedState = null;
try {
  storedState = JSON.parse(window.localStorage.getItem('state') || '');
  if (!storedState || typeof storedState !== 'object' || !storedState.search) {
    storedState = null;
  }
} catch (e) {
  console.warn('failed loading stored state', e);
}

if (window.location.search.includes('no-stored')) {
  storedState = null;
}

export const trainStore = observable(Object.assign({}, initialState, storedState) as State);
trainStore.search.searching = false;
trainStore.dialog = Dialog.NONE;
for (const train of trainStore.myTrains) {
  train.updating = false;
}

// persist state
autorun(persistState, {delay: 3000});
window.addEventListener('unload', persistState);

const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

class Engine {
  private searchResetSubscription: IReactionDisposer;
  private searchResetTimer?: number;

  constructor() {
    this.searchResetSubscription = autorun(() => {
      if (trainStore.search.current.hour !== 'now' || trainStore.search.current.date !== 'today') {
        clearTimeout(this.searchResetTimer);
        this.searchResetTimer = setTimeout(() => this.resetSearchToNow(), 1000 * 60 * 10);
      }
    });
  }

  public searchHourChange(hour: string) {
    if (trainStore.search.searching) return;
    console.log('searchHourChange', hour);
    trainStore.search.current.hour = hour;
  }

  public searchDateChange(date: string) {
    if (trainStore.search.searching) return;
    console.log('searchDateChange', date);
    trainStore.search.current.date = date;
  }

  public async search() {
    if (trainStore.search.searching) return;
    trainStore.search.searching = true;
    if (!trainStore.search.history.toIds.includes(trainStore.search.current.toId)) {
      trainStore.search.history.toIds.push(trainStore.search.current.toId);
    }
    if (!trainStore.search.history.fromIds.includes(trainStore.search.current.fromId)) {
      trainStore.search.history.fromIds.push(trainStore.search.current.fromId);
    }

    trainStore.search.results = null;

    const {fromId, toId, date: datePreset, hour: hourPreset} = trainStore.search.current;
    const {date} = parseDatePreset(datePreset);
    const {hourStr} = parseHourPreset(hourPreset);
    const params = {
      ...trainStore.search.current,
      hour: hourStr,
      date: date.getTime()
    };

    try {
      const rides = await TrainAPI.searchRides({
        fromId,
        toId,
        date,
        hour: hourStr
      });
      trainStore.search.results = {params, rides};
    } catch (e) {
      trainStore.search.error = 'Error while fetching result';
      console.error('Failed searching', e);
    } finally {
      trainStore.search.searching = false;
    }
  }

  public async addTrain(expiry: number = 0) {
    const {fromId, toId, date: datePreset, hour: hourPreset} = trainStore.search.current;
    const {hourStr} = parseHourPreset(hourPreset);

    let id;
    do {
      const rand = Math.random().toString(36).substr(3);
      if (!trainStore.myTrains.some((x) => x.id === rand)) {
        id = rand;
      }
    } while (!id);

    trainStore.myTrains.push({
      name: '',
      id,
      fromId,
      toId,
      hour: hourStr,
      expiry,
      updatedTime: 0,
      updating: false,
      nowRides: []
    });
  }

  public removeTrain(id: string) {
    const index = trainStore.myTrains.findIndex((x) => x.id === id);
    if (index === -1) return;

    trainStore.myTrains.splice(index, 1);
  }

  public renameTrain(id: string, newName: string) {
    const item = trainStore.myTrains.find((x) => x.id === id);
    if (!item) return;

    item.name = newName;
  }

  public searchFromStationChange(stationId: string) {
    if (trainStore.search.searching) return;
    console.log('searchFromStationChange', stationId);
    trainStore.search.current.fromId = stationId;
  }

  public searchToStationChange(stationId: string) {
    if (trainStore.search.searching) return;
    console.log('searchToStationChange', stationId);
    trainStore.search.current.toId = stationId;
  }

  public async updateTrainData(train: MyTrainInfo): Promise<boolean> {
    const {fromId, toId} = train;

    if (train.updating) return false;
    if (Date.now() - train.updatedTime < 1000 * 60 * 5) return false;
    train.updating = true;

    try {
      const {date} = parseDatePreset('today');
      const {hourStr: hour} = parseHourPreset('now');

      const rides = await TrainAPI.searchRides({
        fromId,
        toId,
        date,
        hour
      });

      train.nowRides.splice(0, Infinity);
      train.nowRides.push(...rides);
      train.updatedTime = Date.now();
      return true;
    } catch (e) {
      console.error('Failed updating train', train.name, e);
      throw e;
    } finally {
      train.updating = false;
    }
  }

  private resetSearchToNow() {
    trainStore.search.current.hour = 'now';
    trainStore.search.current.date = 'today';
  }
}

export const engine = new Engine();

export const allStations = Object.keys(STATIONS);

// hoist line

export function getStationName(stationId: string) {
  const normalizedId = String(Number(stationId)); // leading zero mess things up
  if (!STATIONS[normalizedId]) return '-';
  return STATIONS[normalizedId].labels.heb;
}

export function parseDatePreset(datePreset: string) {
  const [base, addition] = datePreset.split('+');
  let date: Date;
  if (base === 'today') {
    date = new Date();
  } else {
    throw new Error(`Unknown date preset ${datePreset}`);
  }

  let extraHours = 0;

  if (addition) {
    extraHours = Number(addition);
  }

  date.setHours(date.getHours() + extraHours);

  const dateStr = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;

  return {
    date,
    day: days[date.getDay()],
    dateStr
  };
}

export function parseHourPreset(hourPreset: string) {
  let hourStr;
  if (hourPreset.startsWith('now')) {
    const [nowStr, diffMinutes = '0'] = hourPreset.split('+');
    const date = new Date(Date.now() + (Number(diffMinutes) * 60000));
    if (!date.getTime()) throw new Error(`Invalid hour preset ${hourPreset}`);
    hourStr = `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}`;
  } else if (/^\d{1,2}:\d{1,2}$/.test(hourPreset)) {
    hourStr = hourPreset;
  } else {
    throw new Error(`Unknown hour preset ${hourPreset}`);
  }

  return {hourStr};
}

export function calculateMinutesFromDate(ride: TrainRideInfo, date: Date = new Date()) {
  const [day, month, year] = ride.fromDate.split('-');
  const [hoursStr, minutesStr] = ride.fromHour.split(':');
  const rideTime = new Date(Number(year), Number(month) - 1, Number(day));
  rideTime.setHours(Number(hoursStr));
  rideTime.setMinutes(Number(minutesStr));

  return Math.round((date.getTime() - rideTime.getTime()) / 60000);
}

export function showDialog(dialogId: Dialog, cb: (result: any) => void = noop) {
  trainStore.dialog = dialogId;
  trainStore.dialogCb = cb;
}

export function hideDialog(result?: any) {
  trainStore.dialog = Dialog.NONE;
  if (trainStore.dialogCb) {
    trainStore.dialogCb(result);
    trainStore.dialogCb = null;
  }
}

function persistState() {
  try {
    window.localStorage.setItem('state', JSON.stringify(toJS(trainStore)));
  } catch (e) {
    console.warn('Failed persisting state', e);
  }
}

interface State {
  myTrains: MyTrainInfo[];
  search: {
    searching: boolean;
    error: string;
    results: {
      params: {
        fromId: string;
        toId: string;
        hour: string; // "now" | "now+minutes" | "12:00"
        date: number; // timestamp
      };
      rides: TrainRideInfo[]
    } | null;
    current: {
      fromId: string;
      toId: string;
      hour: string; // "now" | "now+minutes" | "12:00"
      date: string; // ""
    };
    history: {
      fromIds: string[];
      toIds: string[];
      hour: string;
      date: string;
      favoriteFromStations: {
        [stationId: string]: number;
      };
      favoriteToStations: {
        [stationId: string]: number;
      };
    };
    config: {
      hourPresets: Array<{
        label: string;
        value: string; // "now" | "now+minutes" | "12:00"
      }>;
      datePresets: Array<{
        label: string;
        value: string; // "today" | "today+hours"
      }>
    };
  };
  dialog: Dialog;
  dialogCb: ((result: any) => void) | null;
}

export interface MyTrainInfo {
  name: string;
  id: string;
  fromId: string;
  toId: string;
  hour: string;
  expiry: number;
  updating: boolean;
  updatedTime: number;
  nowRides: TrainRideInfo[];
}

export interface TrainRideInfo {
  id: string;
  active: boolean;
  fromId: string;
  fromHour: string; // '14:37'
  fromDate: string; // '18-7-2019'
  fromPlatform: string;
  toId: string;
  toHour: string;
  durationMinutes: number;
  toPlatform: string;
  load: number; // 0-100
  status: 'on-time' | 'delayed' | 'canceled';
  delayMinutes: number;
  tags: RideTags[];
  switches?: Array<{
    stationId: string;
    waitTimeMinutes: number;
    fromPlatform: string;
    toPlatform: string;
  }>;
  originId: string;
  destinationId: string;
  minutesFromSearch: number;
}
